'use client';

import React, { createContext, useContext, useState, useMemo } from 'react';
import { useIsMobile, useScrollLock } from '@/hooks';

export type MenuContextType = {
  isMobile: boolean;
  isMenuOpen: boolean;
  setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  popOverStates: Record<string, boolean>;
  toggleSideBar: () => void;
  togglePopOverMenu: (key: string) => void;
};

const MenuContext = createContext<MenuContextType | undefined>(undefined);

export const MenuProvider = ({ children }: { children: React.ReactNode }) => {
  const isMobile = useIsMobile({ width: 1024 });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [popOverStates, setPopOverStates] = useState<Record<string, boolean>>(
    {},
  );

  useScrollLock(isMenuOpen && isMobile);

  // Memoize callbacks to prevent unnecessary re-renders
  const toggleSideBar = useMemo(
    () => () => {
      setIsMenuOpen((prev) => !prev);
    },
    [],
  );

  const togglePopOverMenu = useMemo(
    () => (key: string) => {
      setPopOverStates((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
    },
    [],
  );

  // Memoize context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo(
    () => ({
      isMobile,
      isMenuOpen,
      setIsMenuOpen,
      popOverStates,
      toggleSideBar,
      togglePopOverMenu,
    }),
    [isMobile, isMenuOpen, popOverStates, toggleSideBar, togglePopOverMenu],
  );

  return (
    <MenuContext.Provider value={contextValue}>{children}</MenuContext.Provider>
  );
};

export const useMenuContext = () => {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('useMenuContext must be used within a MenuProvider');
  }
  return context;
};
